/**
 * market_client.ts — the located-market slice's rows on the client (space campaign J, 2026-09-30): one subscription per
 * connection, guarded readers (a pre-publish server, a bed, a churning cache never throw), nothing computed that the
 * server already decided (THE DRY-CHAIN LAW: the health card and the summaries are the server's rows).
 */
import { atlas } from '../state.js';
import { GUILD_VAULT_BASE, MASTER_TITLE, isCouncilTitle } from './guild_rules.js';
import type { BookLevel } from '@shared/market_compare.js';

interface Id { isEqual(o: unknown): boolean; toHexString(): string }
interface Ts { microsSinceUnixEpoch: bigint }
export interface SummaryRow {
  key: string; stationKey: string; familyKey: string; bestBid: bigint; bidUnits: number; bidOrders: number; bestAsk: bigint;
  askUnits: number; askOrders: number; levelsJson: string; bookRev: bigint; fills: number; pairs: number; excluded: number;
  medianSov: bigint; vwapX100: bigint; changedAt: Ts;
}
export interface ReceiptRow {
  receiptId: bigint; owner: Id; actor: Id; kind: string; stationKey: string; familyKey: string; requested: number; filled: number;
  costSov: bigint; brokerSov: bigint; escrowSov: bigint; refundSov: bigint; detailJson: string; note: string; at: Ts;
}
export interface FabRow {
  jobId: bigint; owner: Id; stationKey: string; processKey: string; processVersion: number; profile: string; runs: number;
  inputFamily: string; inputName: string; inputStatsJson: string; inputQty: number; ceiling: number; durationS: number;
  state: string; reservedAt: Ts; startedAt: Ts; readyAt: Ts; endedAt: Ts; outputsJson: string; note: string;
}
export interface HealthRow { key: string; kind: string; civ: string; label: string; stationKey: string; numbersJson: string; sourcesJson: string; checkedAt: Ts }
interface Db {
  marketSummary?: { iter(): Iterable<SummaryRow> };
  marketObservation?: { iter(): Iterable<{ stationKey: string; observedAt: Ts; families: number }> };
  marketBookRev?: { iter(): Iterable<{ key: string; stationKey: string; familyKey: string; rev: bigint }> };
  marketReceipt?: { iter(): Iterable<ReceiptRow> };
  fabJob?: { iter(): Iterable<FabRow> };
  econHealth?: { iter(): Iterable<HealthRow> };
  simTickLedger?: { iter(): Iterable<{ name: string; seq: bigint; lastAt: Ts }> };
  guild?: { iter(): Iterable<{ guildId: bigint; name: string; tag: string }> };
  guildMember?: { iter(): Iterable<{ guildId: bigint; identity: Id; title: string }> };
  cargoLot?: { iter(): Iterable<{ lotId: bigint; owner: Id; familyKey: string; name: string; statsJson: string; qty: number; locKind: string; locId: string; tag: string }> };
}
const db = (): Db | null => (atlas.conn ? (atlas.conn.db as unknown as Db) : null);
export const safe = <T>(f: () => T, fb: T): T => { try { return f(); } catch { return fb; } };
export const usOf = (t: Ts | undefined): number => (t ? Number(t.microsSinceUnixEpoch / 1000n) : 0);

let subscribed = false;
const vaultSubs = new Set<string>();
/** Subscribe the slice's rows once per connection (a second handle beside the sector's is harmless). */
export function ensureMarketRows(): void {
  const conn = atlas.conn as unknown as { subscriptionBuilder?: () => { subscribe(q: string[]): unknown } } | null;
  if (!conn?.subscriptionBuilder || subscribed) return;
  subscribed = true;
  try {
    conn.subscriptionBuilder().subscribe([
      'SELECT * FROM market_summary', 'SELECT * FROM market_observation', 'SELECT * FROM market_book_rev',
      'SELECT * FROM market_receipt', 'SELECT * FROM fab_job', 'SELECT * FROM econ_health', 'SELECT * FROM sim_tick_ledger',
    ]);
  } catch { subscribed = false; /* pre-publish server */ }
}
/** The guild's warehouse lots are owned by its VAULT — subscribe them once the dock knows the guild. */
export function ensureVaultLots(vaultHex: string): void {
  const conn = atlas.conn as unknown as { subscriptionBuilder?: () => { subscribe(q: string[]): unknown } } | null;
  if (!conn?.subscriptionBuilder || vaultSubs.has(vaultHex) || !/^[0-9a-f]{64}$/.test(vaultHex)) return;
  vaultSubs.add(vaultHex);
  try { conn.subscriptionBuilder().subscribe([`SELECT * FROM cargo_lot WHERE owner = 0x${vaultHex}`]); } catch { vaultSubs.delete(vaultHex); }
}

export function summaryOf(stationKey: string, familyKey: string): SummaryRow | null {
  return safe(() => { for (const s of db()?.marketSummary?.iter() ?? []) if (s.stationKey === stationKey && s.familyKey === familyKey) return s; return null; }, null);
}
export function summariesAt(stationKey: string): SummaryRow[] {
  return safe(() => [...(db()?.marketSummary?.iter() ?? [])].filter((s) => s.stationKey === stationKey), []);
}
/** When the server last observed a station's book (ms), the sweep's own clock under '*'; null = never. */
export function observedAtMs(stationKey: string): number | null {
  return safe(() => { for (const o of db()?.marketObservation?.iter() ?? []) if (o.stationKey === stationKey) return usOf(o.observedAt); return null; }, null);
}
export function bookRev(stationKey: string, familyKey: string): bigint {
  return safe(() => { for (const r of db()?.marketBookRev?.iter() ?? []) if (r.stationKey === stationKey && r.familyKey === familyKey) return r.rev; return 0n; }, 0n);
}
export function levelsOf(s: SummaryRow | null): { bids: BookLevel[]; asks: BookLevel[] } {
  if (!s) return { bids: [], asks: [] };
  try {
    const j = JSON.parse(s.levelsJson) as { b: Array<[string, number, number]>; a: Array<[string, number, number]> };
    const lv = (x: Array<[string, number, number]>): BookLevel[] => x.map(([p, u, o]) => ({ priceSov: BigInt(p), units: u, orders: o }));
    return { bids: lv(j.b ?? []), asks: lv(j.a ?? []) };
  } catch { return { bids: [], asks: [] }; }
}
export function receiptsOf(owner: Id | null, kinds?: readonly string[]): ReceiptRow[] {
  if (!owner) return [];
  return safe(() => [...(db()?.marketReceipt?.iter() ?? [])].filter((r) => r.owner.isEqual(owner) && (!kinds || kinds.includes(r.kind))).sort((a, b) => (a.receiptId > b.receiptId ? -1 : 1)), []);
}
export function receiptsByHex(ownerHex: string, kinds?: readonly string[]): ReceiptRow[] {
  return safe(() => [...(db()?.marketReceipt?.iter() ?? [])].filter((r) => r.owner.toHexString().replace(/^0x/, '').toLowerCase() === ownerHex && (!kinds || kinds.includes(r.kind))).sort((a, b) => (a.receiptId > b.receiptId ? -1 : 1)), []);
}
export function myJobs(me: Id | null): FabRow[] {
  if (!me) return [];
  return safe(() => [...(db()?.fabJob?.iter() ?? [])].filter((j) => j.owner.isEqual(me)).sort((a, b) => (a.jobId > b.jobId ? -1 : 1)), []);
}
export function jobsAt(stationKey: string): FabRow[] {
  return safe(() => [...(db()?.fabJob?.iter() ?? [])].filter((j) => j.stationKey === stationKey), []);
}
export function healthRows(): HealthRow[] {
  return safe(() => [...(db()?.econHealth?.iter() ?? [])].sort((a, b) => a.key.localeCompare(b.key)), []);
}
export function simTick(): { seq: bigint; atMs: number } | null {
  return safe(() => { for (const r of db()?.simTickLedger?.iter() ?? []) if (r.name === 'npc_sim_tick') return { seq: r.seq, atMs: usOf(r.lastAt) }; return null; }, null);
}

/** The viewer's guild and whether they sit on its court (the Guild Master or a council seat). */
export function myGuild(me: Id | null): { guildId: bigint; name: string; tag: string; court: boolean; vaultHex: string } | null {
  if (!me) return null;
  return safe(() => {
    for (const m of db()?.guildMember?.iter() ?? []) {
      if (!m.identity.isEqual(me)) continue;
      let name = `guild ${m.guildId}`, tag = '';
      for (const g of db()?.guild?.iter() ?? []) if (g.guildId === m.guildId) { name = g.name; tag = g.tag; }
      return { guildId: m.guildId, name, tag, court: m.title === MASTER_TITLE || isCouncilTitle(m.title), vaultHex: vaultHexOf(m.guildId) };
    }
    return null;
  }, null);
}
export function vaultHexOf(guildId: bigint): string { return (GUILD_VAULT_BASE + guildId).toString(16).padStart(64, '0'); }
export function vaultLotsAt(vaultHex: string, stationKey: string): Array<{ lotId: bigint; familyKey: string; name: string; qty: number }> {
  return safe(() => [...(db()?.cargoLot?.iter() ?? [])].filter((l) => l.owner.toHexString().replace(/^0x/, '').toLowerCase() === vaultHex && l.locKind === 'station' && l.locId === stationKey && !l.tag), []);
}
/** "4 min ago" / "just now" / "2 h ago". */
export function ago(ms: number, nowMs = Date.now()): string {
  const s = Math.max(0, Math.round((nowMs - ms) / 1000));
  if (s < 20) return 'just now';
  if (s < 90) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min ago`;
  return `${Math.floor(m / 60)} h ${m % 60} min ago`;
}
export function dur(s: number): string {
  const v = Math.max(0, Math.round(s));
  if (v < 90) return `${v} s`;
  const m = Math.floor(v / 60), r = v % 60;
  if (m < 90) return r ? `${m} min ${r} s` : `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}
